import { collect } from "../graph/paging.ts";
import { MODULE_SCOPES, type ModuleId } from "../auth/config.ts";
import { buildForest, type ScanNode, type FlatItem } from "./tree.ts";
import { chatLabel } from "../modules/teams/paths.ts";
import { relativeFolderSegments } from "../modules/onedrive/paths.ts";
import type { Chat } from "../modules/teams/types.ts";
import type { MailFolder } from "../modules/mail/types.ts";
import type { DriveItemDelta } from "../modules/onedrive/types.ts";
import type { Calendar } from "../modules/calendar/types.ts";
import type { TodoTaskList } from "../modules/tasks/types.ts";
import type { Notebook, OneNoteSection } from "../modules/onenote/types.ts";

/**
 * The Scan step (ARCHITECTURE.md §15, two-step scan → select → export). Each
 * function lists a module's *containers* — the things the user picks from —
 * without downloading any content: chats, the mail folder tree, drive folders,
 * calendars, task lists, OneNote notebooks/sections. Metadata only, using
 * scopes already granted, so scanning is cheap relative to the export it
 * precedes.
 *
 * Container ids here are exactly what each module's export filters on
 * (isContainerSelected), so a selection made from these nodes maps straight
 * through — except OneDrive, whose ids are folder *paths* (its export has no
 * per-folder id to match, only each file's path), and OneNote, whose leaves
 * are section ids (what its export iterates).
 */

export interface ScanOptions {
    signal?: AbortSignal;
    /** For labelling 1:1 chats by the other participant. */
    selfUserId?: string;
}

function opts(o: ScanOptions, service: "outlook" | "drive", scopes: readonly string[]) {
    return { scopes, service, ...(o.signal ? { signal: o.signal } : {}) };
}

async function scanTeams(o: ScanOptions): Promise<ScanNode[]> {
    const chats = await collect<Chat>(
        "/me/chats?$expand=members&$top=50",
        opts(o, "outlook", MODULE_SCOPES.teams)
    );
    return chats.map((chat) => ({
        id: chat.id,
        label: chatLabel(chat, o.selfUserId),
        hint: chat.chatType,
        children: []
    }));
}

async function scanMail(o: ScanOptions): Promise<ScanNode[]> {
    const folders = await collect<MailFolder>("/me/mailFolders/delta", opts(o, "outlook", MODULE_SCOPES.mail));
    const items: FlatItem[] = folders.map((f) => ({
        id: f.id,
        label: f.displayName,
        ...(f.parentFolderId ? { parentId: f.parentFolderId } : {})
    }));
    return buildForest(items);
}

async function scanOneDrive(o: ScanOptions): Promise<ScanNode[]> {
    const items = await collect<DriveItemDelta>("/me/drive/root/delta", opts(o, "drive", MODULE_SCOPES.onedrive));

    // A delta feed can return the same drive item more than once (and a moved
    // item under two paths); keep the last occurrence per id, which is its
    // current state, before turning folders into a tree.
    const latest = new Map<string, DriveItemDelta>();
    for (const item of items) {
        latest.set(item.id, item);
    }

    const flat: FlatItem[] = [];
    for (const item of latest.values()) {
        if (item.deleted || !item.folder || !item.name) {
            continue;
        }
        // id is the folder's own relative path, since the OneDrive export
        // matches files by path, not by drive-item id.
        const parentSegments = relativeFolderSegments(item.parentReference?.path);
        const path = [...parentSegments, item.name].join("/");
        const parentPath = parentSegments.join("/");
        flat.push({ id: path, label: item.name, ...(parentPath ? { parentId: parentPath } : {}) });
    }
    return buildForest(flat);
}

async function scanCalendar(o: ScanOptions): Promise<ScanNode[]> {
    const calendars = await collect<Calendar>("/me/calendars", opts(o, "outlook", MODULE_SCOPES.calendar));
    return calendars.map((c) => ({ id: c.id, label: c.name || "Calendar", children: [] }));
}

async function scanTasks(o: ScanOptions): Promise<ScanNode[]> {
    const lists = await collect<TodoTaskList>("/me/todo/lists", opts(o, "outlook", MODULE_SCOPES.tasks));
    return lists.map((l) => ({ id: l.id, label: l.displayName || "List", children: [] }));
}

async function scanContacts(): Promise<ScanNode[]> {
    // The delegated Contacts export covers the single default folder; there is
    // nothing to branch, so one node stands for "all contacts" — unchecking it
    // skips the module.
    return [{ id: "contacts", label: "All contacts", children: [] }];
}

async function scanOneNote(o: ScanOptions): Promise<ScanNode[]> {
    const notebooks = await collect<Notebook>("/me/onenote/notebooks", opts(o, "outlook", MODULE_SCOPES.onenote));
    const roots: ScanNode[] = [];
    for (const notebook of notebooks) {
        const sections = await collect<OneNoteSection>(
            `/me/onenote/notebooks/${notebook.id}/sections`,
            opts(o, "outlook", MODULE_SCOPES.onenote)
        );
        roots.push({
            // A branch — selection is driven by the section-id leaves the
            // OneNote export actually iterates.
            id: `notebook:${notebook.id}`,
            label: notebook.displayName,
            children: sections.map((s) => ({ id: s.id, label: s.displayName, children: [] }))
        });
    }
    return roots;
}

export const SCANNERS: Record<ModuleId, (o: ScanOptions) => Promise<ScanNode[]>> = {
    teams: scanTeams,
    mail: scanMail,
    onedrive: scanOneDrive,
    calendar: scanCalendar,
    tasks: scanTasks,
    contacts: scanContacts,
    onenote: scanOneNote
};

export function scanModule(moduleId: ModuleId, o: ScanOptions): Promise<ScanNode[]> {
    return SCANNERS[moduleId](o);
}
